import { motion } from "framer-motion";
import BasePage from "../components/BasePage";
import Whitepaper from "../components/Whitepaper";
import Hero from "../components/Hero";

import imgR from "../assets/img/img-hero-r.webp";
import imgL from "../assets/img/img-hero-l.webp";

// SectionCard: Animated card wrapper for each section (title + content)
const SectionCard = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    whileHover={{ scale: 1.03, y: -5 }}
    transition={{
      duration: 0.4,
      ease: "easeOut",
      type: "spring",
      stiffness: 200,
      damping: 20,
    }}
    viewport={{ once: true, amount: 0.2 }}
    className="bg-[var(--color-primary-bg)]/90 backdrop-blur-sm shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 h- flex flex-col justify-center p-20 text-justify"
  >
    <h3 className="text-2xl font-bold text-[var(--color-secondary)] mb-2">
      {title}:
    </h3>
    {children}
  </motion.div>
);

const Paragraph = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <p className={`text-lg text-[var(--color-text-secondary)] ${className}`}>
    {children}
  </p>
);

const MissionVision: React.FC = () => {
  const sections = [
    {
      title: "Our Mission",
      description:
        "To revolutionize digital democracy by creating a decentralized, transparent, and tamper-proof ecosystem where individuals can freely express their opinions without media bias or centralized control, fostering community-driven discourse on key societal topics.",
    },
    {
      title: "Our Vision",
      description:
        "Our vision is to empower individuals by leveraging blockchain technology to provide a secure, gamified, and censorship-resistant platform for opinion-sharing and voting. Through innovative tokenomics and smart contracts, we promote free speech, enhance trust in public decision-making, and build a global community where every voice matters.",
    },
  ];

  return (
    <BasePage
      title="Mission & Vision - UcWaves"
      description="Discover the mission and vision of UcWaves. Our mission is to revolutionize digital democracy and our vision is to create a world with accessible financial freedom through blockchain technology."
      keywords="Mission, Vision, UcWaves, blockchain, digital democracy, decentralized"
    >
      {/* Hero Section */}
      <div className="relative">
        <Hero />
      </div>
      <main>
        <section className="relative py-10 px-4 sm:px-6 lg:px-8 max-w-screen-2xl mx-auto text-justify">
          <img
            src={imgL}
            alt=""
            aria-hidden="true"
            className="hidden md:block absolute left-4 md:left-16 xl:left-32  top-5 transform -translate-y-5  w-24 md:w-40 xl:w-64 animate-fade-in-up object-contain z-10"
          />
          {/* Decorative image right */}
          <img
            src={imgR}
            alt=""
            aria-hidden="true"
            className="hidden md:block absolute right-4 md:right-16 xl:right-32 top-5 transform -translate-y-5 w-24 md:w-40 xl:w-64 animate-fade-in-up object-contain z-10"
          />

          {/* Content wrapper to ensure above images */}
          <div className="relative z-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.5,
                ease: "easeOut",
                type: "spring",
                stiffness: 200,
                damping: 20,
              }}
              viewport={{ once: true }}
              className="flex items-center justify-center"
            >
              <h1 className="text-6xl sm:text-6xl md:text-[52px]  font-bold text-secondary-light text-center  shadow-red-700 mb-32 ">
                Mission & Vision
              </h1>
            </motion.div>
          </div>
        </section>
        {/* Mission and Vision Sections */}
        <section id="mission-vision" className="container mx-auto m-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {sections.map(({ title, description }, index) => (
              <SectionCard key={index} title={title}>
                <Paragraph>{description}</Paragraph>
              </SectionCard>
            ))}
          </div>
        </section>

        {/* Whitepaper Section */}
        <section className="max-w-6xl mx-auto space-y-20">
          <Whitepaper />
        </section>
      </main>
    </BasePage>
  );
};

export default MissionVision;
